import type { RefObject } from "react";
import { useState, useEffect } from "react";
import useDebounce from "./useDebounce"; // Assuming useDebounce is in the same directory or update path accordingly

interface UseContainerWidthOptions {
  debounceDelay?: number;
}

const useContainerWidth = (containerRef: RefObject<HTMLElement | null>, options?: UseContainerWidthOptions): number | undefined => {
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);
  const debounceDelay = options?.debounceDelay ?? 150;
  const debouncedSetContainerWidth = useDebounce(setContainerWidth, debounceDelay);

  useEffect(() => {
    const currentContainer = containerRef.current;
    if (!currentContainer) {
      setContainerWidth(undefined);
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries[0] && entries[0].contentRect && entries[0].contentRect.width > 0) {
        debouncedSetContainerWidth(entries[0].contentRect.width);
      } else if (currentContainer.offsetWidth > 0) {
        // Fallback for initial load or if contentRect isn't immediately available
        debouncedSetContainerWidth(currentContainer.offsetWidth);
      }
    });

    resizeObserver.observe(currentContainer);
    // Set initial width without debounce to avoid delay on first render
    if (currentContainer.offsetWidth > 0) {
      setContainerWidth(currentContainer.offsetWidth);
    }

    return () => {
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }
    };
  }, [containerRef, debouncedSetContainerWidth]); // Added containerRef to dependency array

  return containerWidth;
};

export default useContainerWidth;
